import {
  get,
  query,
  ref,
  orderByKey,
  limitToLast,
  onValue,
  update,
} from 'firebase/database'
import { database } from '../../../../../services/firebaseStorage'
import cookiesToObject from '../../../../../utils/cookiesHandler'

let notificationsCache = null
let notificationsSubscription = null
let activeListeners = 0
let notificationCallbacks = []

export const listenToNotifications = (callback) => {
  try {
    activeListeners++

    notificationCallbacks.push(callback)

    if (notificationsCache) {
      callback(notificationsCache)
    }

    const { id: userId } = cookiesToObject(document.cookie)
    if (!userId) {
      throw new Error('User ID not found')
    }

    if (notificationsSubscription) {
      const unsubscribe = () => {
        activeListeners--

        const index = notificationCallbacks.indexOf(callback)
        if (index > -1) {
          notificationCallbacks.splice(index, 1)
        }

        if (activeListeners === 0) {
          notificationsSubscription()
          notificationsSubscription = null
          notificationCallbacks = []
        }
      }

      return unsubscribe
    }

    const userNotificationsRef = ref(database, `notifications/${userId}`)
    const optimizedQuery = query(
      userNotificationsRef,
      orderByKey(),
      limitToLast(50),
    )

    notificationsSubscription = onValue(optimizedQuery, (snapshot) => {
      const notifications = []
      snapshot.forEach((childSnapshot) => {
        const data = childSnapshot.val()
        notifications.push({
          id: childSnapshot.key,
          ...data,
          read: data.read ?? data.isRead ?? false,
        })
      })

      const filteredNotifications = notifications
        .filter((n) => n.message)
        .reverse()
      notificationsCache = filteredNotifications

      notificationCallbacks.forEach((cb) => cb(filteredNotifications))
    })

    return () => {
      activeListeners--

      const index = notificationCallbacks.indexOf(callback)
      if (index > -1) {
        notificationCallbacks.splice(index, 1)
      }

      if (activeListeners === 0) {
        notificationsSubscription()
        notificationsSubscription = null
        notificationCallbacks = []
      }
    }
  } catch (error) {
    console.error('Error setting up listener:', error)
    throw error
  }
}

const notifyAllCallbacks = () => {
  if (notificationsCache && notificationCallbacks.length > 0) {
    notificationCallbacks.forEach((callback) => callback(notificationsCache))
  }
}

export const markAsRead = async (notificationId) => {
  try {
    const { id: userId } = cookiesToObject(document.cookie)

    if (!userId || !notificationId) {
      throw new Error('User ID or notification ID is missing')
    }

    if (notificationsCache) {
      notificationsCache = notificationsCache.map((notification) =>
        notification.id === notificationId
          ? { ...notification, read: true }
          : notification,
      )
      notifyAllCallbacks()
    }

    await update(ref(database, `notifications/${userId}/${notificationId}`), {
      read: true,
    })
    return true
  } catch (error) {
    console.error('Error marking notification as read:', error)
    throw new Error('Failed to mark notification as read')
  }
}

export const markAllAsRead = async () => {
  try {
    const { id: userId } = cookiesToObject(document.cookie)

    if (!userId) {
      throw new Error('User ID not found')
    }

    if (notificationsCache) {
      notificationsCache = notificationsCache.map((notification) => ({
        ...notification,
        read: true,
      }))
      notifyAllCallbacks()
    }

    const snapshot = await get(ref(database, `notifications/${userId}`))

    if (!snapshot.exists()) {
      return true
    }

    const updates = {}

    snapshot.forEach((child) => {
      const notification = child.val()
      const isAlreadyRead =
        notification.read === true || notification.isRead === true
      if (!isAlreadyRead) {
        updates[`${child.key}/read`] = true
        //clean up old `isRead` field
        updates[`${child.key}/isRead`] = null
      }
    })

    if (Object.keys(updates).length === 0) {
      return true
    }

    await update(ref(database, `notifications/${userId}`), updates)
    return true
  } catch (error) {
    console.error('Error marking all notifications as read:', error)
    throw new Error('Failed to mark all notifications as read')
  }
}

export const getInitials = (title) => {
  if (!title || typeof title !== 'string') return 'N/A'
  const cleanedTitle = title.trim()
  if (!cleanedTitle) return 'N/A'
  const words = cleanedTitle.split(/\s+/).filter((word) => word.length > 0)
  if (words.length === 0) return 'N/A'
  const first = words[0].charAt(0).toUpperCase()
  const second = words.length > 1 ? words[1].charAt(0).toUpperCase() : ''
  return first + second
}

export const formatTimeAgo = (timestamp) => {
  if (!timestamp) return 'Unknown date'

  let date
  try {
    if (typeof timestamp === 'number') {
      date = new Date(timestamp)
    } else if (timestamp?.seconds) {
      date = new Date(timestamp.seconds * 1000)
    } else if (typeof timestamp === 'string') {
      const utcDateRegex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/
      if (utcDateRegex.test(timestamp)) {
        const isoDateString = timestamp.replace(' ', 'T') + ':00Z'
        date = new Date(isoDateString)
      } else {
        date = new Date(timestamp)
      }
    } else {
      date = new Date(timestamp)
    }

    if (isNaN(date.getTime())) {
      return 'Invalid date'
    }
  } catch (error) {
    console.error('Error parsing timestamp:', error)
    return 'Invalid date'
  }

  const now = new Date()
  const diffMs = now - date
  const diffSec = Math.floor(diffMs / 1000)
  const diffMin = Math.floor(diffSec / 60)
  const diffHour = Math.floor(diffMin / 60)
  const diffDay = Math.floor(diffHour / 24)

  if (diffSec < 60) {
    return 'Just now'
  } else if (diffMin < 60) {
    return diffMin === 1 ? '1 minute ago' : `${diffMin} minutes ago`
  } else if (diffHour < 24) {
    return diffHour === 1 ? '1 hour ago' : `${diffHour} hours ago`
  } else if (diffDay < 7) {
    return diffDay === 1 ? '1 day ago' : `${diffDay} days ago`
  } else {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    })
  }
}
